import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import AdminLayout from '../AdminLayout';
import { useGetUserDetailsQuery, useGetAdminEliteStatusQuery, useGrantWriterRoleMutation, useRevokeWriterRoleMutation, useGrantPartnerApiRoleMutation, useRevokePartnerApiRoleMutation } from 'store/services/admin/adminApi';
import { useGetUserProgressQuery, useTriggerManualEmailMutation } from 'store/services/dripCampaign/dripCampaignApi';
import { MessageType } from 'types/interfaces/message';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import AdjustPointsModal from './components/AdjustPointsModal';
import BanUserModal from './components/BanUserModal';
import ImpersonateUserModal from './components/ImpersonateUserModal';
import UnbanUserModal from './components/UnbanUserModal';
import GrantEliteModal from './components/GrantEliteModal';
import RevokeEliteModal from './components/RevokeEliteModal';
import DeleteAccountModal from './components/DeleteAccountModal';
import { toast } from 'react-toastify';
import { adminUserDetailPath } from 'routes/paths';
import './UserDetailPage.css';

const UserDetailPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [showAdjustPointsModal, setShowAdjustPointsModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [showUnbanModal, setShowUnbanModal] = useState(false);
  const [showEliteModal, setShowEliteModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);

  const { data, isLoading, error, refetch } = useGetUserDetailsQuery(
    parseInt(userId || '0', 10),
    { skip: !userId }
  );

  const { data: eliteData } = useGetAdminEliteStatusQuery(
    parseInt(userId || '0', 10),
    { skip: !userId }
  );
  const eliteInfo = eliteData?.elite;

  const { data: campaignProgress, isLoading: isCampaignLoading } = useGetUserProgressQuery(
    parseInt(userId || '0', 10),
    { skip: !userId }
  );

  const [triggerManualEmail, { isLoading: isTriggeringEmail }] = useTriggerManualEmailMutation();
  const [grantWriter, { isLoading: isGrantingWriter }] = useGrantWriterRoleMutation();
  const [revokeWriter, { isLoading: isRevokingWriter }] = useRevokeWriterRoleMutation();
  const [grantPartnerApi, { isLoading: isGrantingPartnerApi }] = useGrantPartnerApiRoleMutation();
  const [revokePartnerApi, { isLoading: isRevokingPartnerApi }] = useRevokePartnerApiRoleMutation();

  const handleManualTrigger = async (messageType: MessageType) => {
    if (!userId) return;

    try {
      const result = await triggerManualEmail({
        userId: parseInt(userId, 10),
        messageType
      }).unwrap();

      if (result.success) {
        toast.success(result.message || 'Email sent successfully');
      } else {
        toast.error(result.message || 'Failed to send email');
      }
    } catch (error: any) {
      console.error('Failed to trigger manual email:', error);
      toast.error(error?.data?.message || 'Failed to send email');
    }
  };

  const getMessageTypeName = (type: MessageType) => {
    switch (type) {
      case MessageType.WANT_LIST_REMINDER:
        return 'Want List Reminder';
      case MessageType.EMAIL_VERIFICATION_REMINDER:
        return 'Email Verification';
      case MessageType.PHONE_VERIFICATION_REMINDER:
        return 'Phone Verification';
      case MessageType.TRADE_REMINDER_SHIP:
        return 'Trade Shipping Reminder';
      case MessageType.TRADE_REMINDER_COMPLETE:
        return 'Trade Completion Reminder';
      default:
        return type;
    }
  };

  const handleGrantWriter = async () => {
    if (!user) return;
    try {
      await grantWriter(user.id).unwrap();
      toast.success(`${user.username} has been granted Writer access`);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to grant writer role');
    }
  };

  const handleRevokeWriter = async () => {
    if (!user) return;
    try {
      await revokeWriter(user.id).unwrap();
      toast.success(`Writer access revoked for ${user.username}`);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to revoke writer role');
    }
  };

  const handleGrantPartnerApi = async () => {
    if (!user) return;
    try {
      await grantPartnerApi(user.id).unwrap();
      toast.success(`${user.username} has been granted Partner API access`);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to grant Partner API access');
    }
  };

  const handleRevokePartnerApi = async () => {
    if (!user) return;
    try {
      await revokePartnerApi(user.id).unwrap();
      toast.success(`Partner API access revoked for ${user.username}`);
      refetch();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to revoke Partner API access');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatPoints = (points: number) => {
    return points.toLocaleString();
  };

  if (isLoading) {
    return (
      <AdminLayout title="User Details" currentPath="/admin/users">
        <div className="user-detail-page">
          <div className="user-detail-loading">Loading user details...</div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !data) {
    return (
      <AdminLayout title="User Details" currentPath="/admin/users">
        <div className="user-detail-page">
          <div className="user-detail-error">
            {(error as any)?.status === 404
              ? 'User not found.'
              : 'Failed to load user details. Please try again.'}
          </div>
          <button onClick={() => navigate('/admin/users')} className="back-button">
            ← Back to Users
          </button>
        </div>
      </AdminLayout>
    );
  }

  const user = data.user;
  const isDeleted = user.status === 'deleted';

  return (
    <AdminLayout title={`User Details - ${user.username}`} currentPath="/admin/users">
      <div className="user-detail-page">
        {/* Header */}
        <div className="user-detail-header">
          <button onClick={() => navigate('/admin/users')} className="back-button">
            ← Back to Users
          </button>
          <div className="header-info">
            <div className="header-left">
              <AvatarWithFlag
                avatarUrl={user.avatarUrl}
                username={user.username}
                size={64}
              />
              <div className="header-text">
                <div className="header-title-row">
                  <h1 className="user-detail-title">{user.username}</h1>
                  {user.roles?.includes('admin') && (
                    <span className="role-badge admin-badge">Admin</span>
                  )}
                  {user.roles?.includes('writer') && (
                    <span className="role-badge writer-badge">Writer</span>
                  )}
                  {user.roles?.includes('partner-api') && (
                    <span className="role-badge partner-api-badge">Partner API</span>
                  )}
                </div>
                <span className="user-id">User ID: #{user.id}</span>
              </div>
            </div>
            <div className="header-right">
              <div className="points-badge">
                <div className="points-icon" />
                <span className="points-value">{formatPoints(user.points)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ban Banner (conditional) */}
        {user.isBanned && user.bannedAt && user.bannedBy && user.banReason && (
          <div className="ban-status-banner">
            <div className="ban-banner-content">
              <div className="ban-banner-header">
                <span className="ban-icon">⚠️</span>
                <strong>USER BANNED</strong>
              </div>
              <div className="ban-details">
                <div className="ban-detail-item">
                  <span className="ban-label">Banned by:</span>
                  <span className="ban-value">{user.bannedBy.username}</span>
                </div>
                <div className="ban-detail-item">
                  <span className="ban-label">Date:</span>
                  <span className="ban-value">{formatDate(user.bannedAt)}</span>
                </div>
              </div>
              <div className="ban-reason">
                <span className="ban-reason-label">Reason:</span>
                <p className="ban-reason-text">{user.banReason}</p>
              </div>
              <button
                className="unban-button"
                onClick={() => setShowUnbanModal(true)}
              >
                Unban User
              </button>
            </div>
          </div>
        )}

        {/* Deleted Banner (conditional) */}
        {isDeleted && (
          <div className="deleted-status-banner">
            <div className="deleted-banner-content">
              <div className="deleted-banner-header">
                <span className="deleted-icon">🗑️</span>
                <strong>USER DELETED</strong>
              </div>
              <div className="deleted-details">
                <div className="deleted-detail-item">
                  <span className="deleted-label">Deleted on:</span>
                  <span className="deleted-value">{formatDate(user.deletedAt ?? null)}</span>
                </div>
              </div>
              <p className="deleted-banner-note">
                This account has been anonymized at the user&apos;s request. Personal
                information has been permanently wiped and the account cannot be restored or
                logged into.
              </p>
            </div>
          </div>
        )}

        {/* User Information Section */}
        <div className="detail-section">
          <h2 className="section-title">User Information</h2>

          <div className="info-grid">
            <div className="info-card">
              <h3 className="info-card-title">Account Status</h3>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-label">Created:</span>
                  <span className="info-value">{formatDate(user.createdAt)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Last Active:</span>
                  <span className="info-value">{formatDate(user.lastLogin)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">
                    {user.email}
                    {user.emailVerified && (
                      <span className="verified-badge">✓ Verified</span>
                    )}
                  </span>
                </div>
                {user.phoneNumber && (
                  <div className="info-row">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">
                      {user.phoneCountryCode} {user.phoneNumber}
                      {user.phoneVerified && (
                        <span className="verified-badge">✓ Verified</span>
                      )}
                    </span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Profile:</span>
                  <span className="info-value">
                    <Link
                      to={`/profile/u/${encodeURIComponent(user.username)}`}
                      style={{ color: '#7c3aed', textDecoration: 'none' }}
                    >
                      View Public Profile →
                    </Link>
                  </span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <h3 className="info-card-title">Shipping Address</h3>
              <div className="address-block">
                {user.shippingAddress1 || user.shippingCity ? (
                  <>
                    {user.shippingAddress1 && <div>{user.shippingAddress1}</div>}
                    {user.shippingAddress2 && <div>{user.shippingAddress2}</div>}
                    {(user.shippingCity || user.shippingState || user.shippingPostalCode) && (
                      <div>
                        {user.shippingCity}
                        {user.shippingCity && user.shippingState && ', '}
                        {user.shippingState} {user.shippingPostalCode}
                      </div>
                    )}
                    {user.shippingCountry && <div>{user.shippingCountry}</div>}
                  </>
                ) : (
                  <div className="no-address">No address on file</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Blocked By Section (admin-only surface for block reasons) */}
        {user.receivedBlocks && user.receivedBlocks.length > 0 && (
          <div className="detail-section">
            <h2 className="section-title">Blocked By</h2>
            <div className="info-card">
              <div className="info-rows">
                {user.receivedBlocks.map((block) => (
                  <div key={`${block.blocker.id}-${block.createdAt}`} className="received-block-item">
                    <div className="info-row">
                      <span className="info-label">Blocked by:</span>
                      <span className="info-value">
                        {block.blocker.username}
                        <span style={{ color: 'rgba(255,255,255,0.5)', marginLeft: 8 }}>
                          {formatDate(block.createdAt)}
                        </span>
                      </span>
                    </div>
                    <p className="received-block-reason">{block.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Elite Status Section */}
        <div className="detail-section">
          <h2 className="section-title">Elite Status</h2>

          {eliteInfo ? (
            <div className="info-card">
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span className="info-value">
                    {eliteInfo.isElite ? (
                      <span className="elite-status-active">
                        <img
                          src={AssetUrls.icons.elite.badge}
                          alt="Elite"
                          style={{ width: 16, height: 16, marginRight: 6, verticalAlign: 'middle' }}
                        />
                        {eliteInfo.expiresAt ? 'Expiring' : 'Active'}
                      </span>
                    ) : (
                      <span className="elite-status-inactive">Not Elite</span>
                    )}
                  </span>
                </div>

                {eliteInfo.isElite && (
                  <>
                    <div className="info-row">
                      <span className="info-label">Granted by:</span>
                      <span className="info-value">{
                        eliteInfo.grantedBy === 'admin' && eliteInfo.stripeSubscriptionStatus === 'active'
                          ? 'Stripe + Admin grant'
                          : eliteInfo.grantedBy === 'stripe'
                            ? 'Stripe subscription'
                            : eliteInfo.grantedBy === 'admin'
                              ? 'Admin grant'
                              : '\u2014'
                      }</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Elite since:</span>
                      <span className="info-value">{
                        eliteInfo.eliteSince
                          ? new Date(eliteInfo.eliteSince).toLocaleDateString('en-US', {
                              month: 'long', day: 'numeric', year: 'numeric'
                            })
                          : '\u2014'
                      }</span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Expires:</span>
                      <span className="info-value">{
                        eliteInfo.expiresAt
                          ? new Date(eliteInfo.expiresAt).toLocaleDateString('en-US', {
                              month: 'long', day: 'numeric', year: 'numeric'
                            })
                          : eliteInfo.stripeSubscriptionStatus === 'active' && eliteInfo.currentPeriodEnd
                            ? `End of billing cycle (${new Date(eliteInfo.currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})`
                            : 'Indefinite'
                      }</span>
                    </div>

                    {eliteInfo.adminNote && (
                      <div className="info-row">
                        <span className="info-label">Admin note:</span>
                        <span className="info-value" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
                          {eliteInfo.adminNote}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Elite status unavailable.</div>
          )}
        </div>

        {/* Trade Activity Section */}
        <div className="detail-section">
          <h2 className="section-title">Trade Activity</h2>

          <div className="trade-stats-grid">
            <div className="trade-stat-card sent">
              <div className="trade-stat-header">
                <h3 className="trade-stat-title">Sent</h3>
              </div>
              <div className="trade-stat-body">
                <div className="trade-stat-main">
                  <span className="trade-stat-number">{user.statistics.cardsSent}</span>
                  <span className="trade-stat-label">cards</span>
                </div>
                <div className="trade-stat-points">
                  <div className="points-icon-small" />
                  <span>{formatPoints(user.statistics.pointsSent)}</span>
                </div>
              </div>
            </div>

            <div className="trade-stat-card received">
              <div className="trade-stat-header">
                <h3 className="trade-stat-title">Received</h3>
              </div>
              <div className="trade-stat-body">
                <div className="trade-stat-main">
                  <span className="trade-stat-number">{user.statistics.cardsReceived}</span>
                  <span className="trade-stat-label">cards</span>
                </div>
                <div className="trade-stat-points">
                  <div className="points-icon-small" />
                  <span>{formatPoints(user.statistics.pointsReceived)}</span>
                </div>
              </div>
            </div>

            <div
              className="trade-stat-card collection clickable"
              onClick={() => navigate(`${adminUserDetailPath(user.id)}/have-list`)}
            >
              <div className="trade-stat-header">
                <h3 className="trade-stat-title">Have List</h3>
              </div>
              <div className="trade-stat-body">
                <div className="trade-stat-main">
                  <span className="trade-stat-number">{user.statistics.haveListCardCount ?? 0}</span>
                  <span className="trade-stat-label">cards</span>
                </div>
              </div>
            </div>

            <div
              className="trade-stat-card collection clickable"
              onClick={() => navigate(`${adminUserDetailPath(user.id)}/want-list`)}
            >
              <div className="trade-stat-header">
                <h3 className="trade-stat-title">Want List</h3>
              </div>
              <div className="trade-stat-body">
                <div className="trade-stat-main">
                  <span className="trade-stat-number">{user.statistics.wantListCardCount ?? 0}</span>
                  <span className="trade-stat-label">cards</span>
                </div>
              </div>
            </div>

            <div className="trade-stat-card">
              <div className="trade-stat-header">
                <h3 className="trade-stat-title">Active Trades</h3>
              </div>
              <div className="trade-stat-body">
                <div className="trade-stat-main">
                  <span className="trade-stat-number">{user.statistics.activeTrades}</span>
                  <span className="trade-stat-label">trades</span>
                </div>
                <div className="trade-breakdown">
                  <div className="breakdown-item">
                    <span className="breakdown-label">Outbound:</span>
                    <span className="breakdown-value">{user.statistics.pendingOutboundTrades}</span>
                  </div>
                  <div className="breakdown-item">
                    <span className="breakdown-label">Inbound:</span>
                    <span className="breakdown-value">{user.statistics.pendingInboundTrades}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="trade-stat-card">
              <div className="trade-stat-header">
                <h3 className="trade-stat-title">Total Completed</h3>
              </div>
              <div className="trade-stat-body">
                <div className="trade-stat-main">
                  <span className="trade-stat-number">{user.statistics.totalTrades}</span>
                  <span className="trade-stat-label">trades</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Drip Campaign Progress Section */}
        <div className="detail-section">
          <h2 className="section-title">Drip Campaign Progress</h2>

          {isCampaignLoading ? (
            <div className="campaign-loading">Loading campaign progress...</div>
          ) : campaignProgress && campaignProgress.progress.length > 0 ? (
            <div className="campaign-progress-grid">
              {campaignProgress.progress.map((item, index) => (
                <div key={index} className="campaign-progress-card">
                  <div className="campaign-progress-header">
                    <h3 className="campaign-progress-title">
                      {item.sentAt ? '✅' : item.errorMessage ? '❌' : '⏳'} {getMessageTypeName(item.type)}
                    </h3>
                  </div>

                  <div className="campaign-progress-body">
                    <div className="campaign-progress-row">
                      <span className="campaign-label">Scheduled:</span>
                      <span className="campaign-value">{formatDate(item.scheduledAt)}</span>
                    </div>

                    {item.sentAt && (
                      <div className="campaign-progress-row">
                        <span className="campaign-label">Sent:</span>
                        <span className="campaign-value campaign-value-success">{formatDate(item.sentAt)}</span>
                      </div>
                    )}

                    {item.errorMessage && (
                      <div className="campaign-progress-row">
                        <span className="campaign-label">Error:</span>
                        <span className="campaign-value campaign-value-error">{item.errorMessage}</span>
                      </div>
                    )}

                    {!item.sentAt && !item.errorMessage && (
                      <div className="campaign-progress-row">
                        <span className="campaign-label">Status:</span>
                        <span className="campaign-value campaign-value-pending">Pending</span>
                      </div>
                    )}

                    <div className="campaign-action-buttons">
                      <button
                        className="campaign-action-button"
                        onClick={() => handleManualTrigger(item.type)}
                        disabled={isTriggeringEmail}
                      >
                        {item.sentAt ? 'Resend Email' : 'Send Now'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="campaign-empty">
              <p>No drip campaign emails scheduled for this user.</p>
              <p className="campaign-empty-hint">
                Drip campaign emails are automatically scheduled for new users based on their account status.
              </p>
            </div>
          )}
        </div>

        {/* Admin Actions Section */}
        <div className="detail-section">
          <h2 className="section-title">Admin Actions</h2>

          <div className="admin-actions-grid">
            <button
              className="admin-action-button purple"
              onClick={() => setShowAdjustPointsModal(true)}
              disabled={isDeleted}
            >
              <span className="action-icon">💰</span>
              <span className="action-text">Adjust Points</span>
            </button>

            <button
              className="admin-action-button blue"
              onClick={() => setShowImpersonateModal(true)}
              disabled={isDeleted}
            >
              <span className="action-icon">🎭</span>
              <span className="action-text">Impersonate User</span>
            </button>

            {user.roles?.includes('writer') ? (
              <button
                className="admin-action-button orange"
                onClick={handleRevokeWriter}
                disabled={isRevokingWriter || isDeleted}
              >
                <span className="action-icon">✏️</span>
                <span className="action-text">{isRevokingWriter ? 'Revoking...' : 'Revoke Writer'}</span>
              </button>
            ) : (
              <button
                className="admin-action-button teal"
                onClick={handleGrantWriter}
                disabled={isGrantingWriter || isDeleted}
              >
                <span className="action-icon">✏️</span>
                <span className="action-text">{isGrantingWriter ? 'Granting...' : 'Grant Writer'}</span>
              </button>
            )}

            {user.roles?.includes('partner-api') ? (
              <button
                className="admin-action-button orange"
                onClick={handleRevokePartnerApi}
                disabled={isRevokingPartnerApi || isDeleted}
              >
                <span className="action-icon">🔌</span>
                <span className="action-text">{isRevokingPartnerApi ? 'Revoking...' : 'Revoke Partner API'}</span>
              </button>
            ) : (
              <button
                className="admin-action-button teal"
                onClick={handleGrantPartnerApi}
                disabled={isGrantingPartnerApi || isDeleted}
              >
                <span className="action-icon">🔌</span>
                <span className="action-text">{isGrantingPartnerApi ? 'Granting...' : 'Grant Partner API'}</span>
              </button>
            )}

            {!user.isBanned ? (
              <button
                className="admin-action-button red"
                onClick={() => setShowBanModal(true)}
                disabled={isDeleted}
              >
                <span className="action-icon">⚠️</span>
                <span className="action-text">Ban User</span>
              </button>
            ) : (
              <button
                className="admin-action-button green"
                onClick={() => setShowUnbanModal(true)}
                disabled={isDeleted}
              >
                <span className="action-icon">✓</span>
                <span className="action-text">Unban User</span>
              </button>
            )}

            {!isDeleted && (
              <button
                className="admin-action-button red"
                onClick={() => setShowDeleteAccountModal(true)}
              >
                <span className="action-icon">🗑️</span>
                <span className="action-text">Delete Account</span>
              </button>
            )}

            <button
              className="admin-action-button orange"
              onClick={() => setShowEliteModal(true)}
              disabled={isDeleted}
            >
              <span className="action-icon">
                <img
                  src={AssetUrls.icons.elite.badge}
                  alt="Elite"
                  style={{ width: 32, height: 32 }}
                />
              </span>
              <span className="action-text">
                {eliteInfo?.isElite ? 'Revoke Elite' : 'Grant Elite'}
              </span>
            </button>
          </div>
        </div>

        {/* Modals */}
        <AdjustPointsModal
          isOpen={showAdjustPointsModal}
          onClose={() => setShowAdjustPointsModal(false)}
          userId={user.id}
          username={user.username}
          currentBalance={user.points}
          onSuccess={() => refetch()}
        />

        <BanUserModal
          isOpen={showBanModal}
          onClose={() => setShowBanModal(false)}
          userId={user.id}
          username={user.username}
          activeTrades={{
            pendingOutboundTrades: user.statistics.pendingOutboundTrades,
            pendingInboundTrades: user.statistics.pendingInboundTrades
          }}
          onSuccess={() => refetch()}
        />

        <DeleteAccountModal
          isOpen={showDeleteAccountModal}
          onClose={() => setShowDeleteAccountModal(false)}
          userId={user.id}
          username={user.username}
          onSuccess={() => refetch()}
        />

        <ImpersonateUserModal
          isOpen={showImpersonateModal}
          onClose={() => setShowImpersonateModal(false)}
          userId={user.id}
          username={user.username}
        />

        {user.isBanned && user.bannedAt && user.bannedBy && user.banReason && (
          <UnbanUserModal
            isOpen={showUnbanModal}
            onClose={() => setShowUnbanModal(false)}
            userId={user.id}
            username={user.username}
            banInfo={{
              bannedAt: user.bannedAt,
              bannedBy: user.bannedBy,
              banReason: user.banReason
            }}
            onSuccess={() => refetch()}
          />
        )}

        {eliteInfo && !eliteInfo.isElite && (
          <GrantEliteModal
            isOpen={showEliteModal}
            onClose={() => setShowEliteModal(false)}
            userId={user.id}
            username={user.username}
            onSuccess={() => refetch()}
          />
        )}

        {eliteInfo?.isElite && (
          <RevokeEliteModal
            isOpen={showEliteModal}
            onClose={() => setShowEliteModal(false)}
            userId={user.id}
            username={user.username}
            eliteInfo={eliteInfo}
            onSuccess={() => refetch()}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default UserDetailPage;
